import React, { useState } from 'react';
import { ActionSheet } from 'antd-mobile';
import type { Action } from 'antd-mobile/es/components/action-sheet';
import { CheckCircleFill, DownOutline, GlobalOutline } from 'antd-mobile-icons';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LOCALE, LOCALE_LABELS, SUPPORTED_LOCALES, type SupportedLocale } from '../i18n';

interface LocaleSwitcherProps {
  style?: React.CSSProperties;
  fullWidth?: boolean;
  tone?: 'light' | 'dark';
  labelMode?: 'full' | 'short';
}

const TONE_STYLE_MAP: Record<NonNullable<LocaleSwitcherProps['tone']>, React.CSSProperties> = {
  light: {
    background: '#ffffff',
    color: '#25324a',
    border: '1px solid rgba(210, 218, 233, 0.96)',
    boxShadow: '0 10px 22px rgba(15, 23, 42, 0.08)',
  },
  dark: {
    background: 'rgba(255, 255, 255, 0.16)',
    color: '#ffffff',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    boxShadow: '0 12px 24px rgba(15, 23, 42, 0.12)',
    backdropFilter: 'blur(14px)',
  },
};

export const LocaleSwitcher: React.FC<LocaleSwitcherProps> = ({
  style,
  fullWidth = false,
  tone = 'light',
  labelMode = 'full',
}) => {
  const { t, i18n } = useTranslation('common');
  const [visible, setVisible] = useState(false);

  const currentLng = (i18n.language as SupportedLocale) || DEFAULT_LOCALE;

  const actions: Action[] = SUPPORTED_LOCALES.map((lng) => ({
    key: lng,
    text: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {lng === currentLng ? <CheckCircleFill color="#3f63d6" fontSize={16} /> : <span style={{ width: 16, height: 16 }} />}
        <span>{LOCALE_LABELS[lng]}</span>
      </span>
    ),
    bold: lng === currentLng,
    onClick: () => {
      void i18n.changeLanguage(lng);
      setVisible(false);
    },
  }));

  const triggerLabel = labelMode === 'short'
    ? LOCALE_LABELS[currentLng]
    : `${t('language')}: ${LOCALE_LABELS[currentLng]}`;

  return (
    <>
      <button
        type="button"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          width: fullWidth ? '100%' : 'auto',
          minHeight: 42,
          padding: fullWidth ? '0 14px' : '0 12px',
          borderRadius: 999,
          fontSize: 13,
          lineHeight: 1,
          fontWeight: 700,
          cursor: 'pointer',
          transition: 'all 160ms ease',
          ...TONE_STYLE_MAP[tone],
          ...style,
        }}
        onClick={() => setVisible(true)}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: tone === 'dark' ? 'rgba(255, 255, 255, 0.16)' : 'rgba(63, 99, 214, 0.1)',
              color: tone === 'dark' ? '#ffffff' : '#3f63d6',
              flexShrink: 0,
            }}
          >
            <GlobalOutline fontSize={14} />
          </span>
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {triggerLabel}
          </span>
        </span>
        <DownOutline
          fontSize={12}
          style={{
            color: tone === 'dark' ? 'rgba(255, 255, 255, 0.82)' : '#7a849b',
            flexShrink: 0,
          }}
        />
      </button>
      <ActionSheet
        visible={visible}
        actions={actions}
        cancelText={t('cancel')}
        onClose={() => setVisible(false)}
      />
    </>
  );
};
